import { admissionDatabaseRequests } from "@/src/api/admissionOfficeDatabaseRequests";
import { officeAccountingPageRequests } from "@/src/api/officeAccountingPageRequests";
import { ITaskListItem, tasksPageRequests } from "@/src/api/tasksPageRequests";
import { tasksStatusConst } from "@/src/constants/tasksConsts";
import { IADatabaseDocument } from "@/src/types/admissionDatabase";
import {
  IApplicantAccountingCreatePayload,
  IApplicantAccountingUpdatePayload,
} from "@/src/types/officeAccounting";
import { formatDateToApiIso } from "@/src/utils/formatDateToApiIso";
import { resolveTaskFormOffice } from "@/src/utils/tasks/buildAdmissionPrefillPayloadFromTask";
import { buildSemesterCostsFromTask } from "@/src/utils/tasks/buildSemesterCostsFromTask";
import type { ExecuteDocumentCheckApproveResult } from "@/src/utils/tasks/executeDocumentCheckApprove";
import type { FormOfficeApi } from "@/src/utils/tasks/normalizeFormOfficeForApi";
import {
  parseTaskApplicantId,
  parseTaskApplicantInfo,
  resolveTaskApiId,
} from "@/src/utils/tasks/parseTaskApplicantInfo";
import { buildSimpleHistoryText, createApplicantHistory } from "@/src/utils/applicants/createApplicantHistory";

const optionalTrimmedField = (value?: string | null): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const assignOptionalString = <T extends string>(
  target: IApplicantAccountingUpdatePayload,
  key: T,
  value?: string | null,
) => {
  const trimmed = optionalTrimmedField(value);
  if (trimmed !== undefined) {
    (target as Record<string, string>)[key] = trimmed;
  }
};

/** Maps create payload (+ task fields) onto update — everything update accepts that we can fill from create/task. */
const buildApplicantAccountingUpdatePayload = (
  createPayload: IApplicantAccountingCreatePayload,
  rawTask: ITaskListItem,
): IApplicantAccountingUpdatePayload => {
  const updatePayload: IApplicantAccountingUpdatePayload = {
    ...createPayload,
  };

  assignOptionalString(updatePayload, "whoose", rawTask.whoose);
  assignOptionalString(updatePayload, "form_of_education", rawTask.form_of_education);
  assignOptionalString(updatePayload, "duration", rawTask.duration);
  assignOptionalString(updatePayload, "medium_of_instruction", rawTask.medium_of_instruction);
  assignOptionalString(updatePayload, "admission_semester", rawTask.admission_semester);
  assignOptionalString(updatePayload, "intake", rawTask.intake);
  assignOptionalString(updatePayload, "category", rawTask.category);
  assignOptionalString(updatePayload, "field_of_study", rawTask.category);
  assignOptionalString(updatePayload, "tuition_fee", rawTask.tuition_fee);
  assignOptionalString(updatePayload, "medical_insurance", rawTask.medical_insurance);
  assignOptionalString(updatePayload, "residence_permit", rawTask.residence_permit);
  assignOptionalString(updatePayload, "other_expenses", rawTask.other_expenses);
  assignOptionalString(updatePayload, "acommodation", rawTask.acommodation);
  assignOptionalString(updatePayload, "admission_fee", rawTask.admission_fee);
  assignOptionalString(updatePayload, "complete_one_year", rawTask.complete_one_year);
  assignOptionalString(updatePayload, "selected_course", rawTask.selected_course);
  assignOptionalString(updatePayload, "transfer_fee", rawTask.transfer_fee);

  if (typeof rawTask.tuition_fee_flag === "boolean") {
    updatePayload.tuition_fee_flag = rawTask.tuition_fee_flag;
  }

  return updatePayload;
};

export type CurrentMainDocumentMode = "register" | "switch";

export const resolveDocumentUserId = (document: IADatabaseDocument): number | null => {
  const raw = document.user_id;
  if (raw == null || raw === "") return null;
  const parsed = typeof raw === "number" ? raw : Number(String(raw).trim());
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
};

export const resolveCurrentMainDocValue = (value?: string | null): string => {
  return typeof value === "string" ? value.trim() : "";
};

export const isCurrentMainEligibleDocumentType = (documentType?: string | null): boolean => {
  const normalized = String(documentType ?? "").trim().toLowerCase();
  return normalized === "admission" || normalized === "invoice";
};

export type CurrentMainDocumentButtonKind = "registration" | "switch" | "main";

export const resolveCurrentMainDocumentButtonKind = (
  document: IADatabaseDocument,
): CurrentMainDocumentButtonKind | null => {
  if (!isCurrentMainEligibleDocumentType(document.document_type)) return null;

  const currentMainDoc = resolveCurrentMainDocValue(document.current_main_doc);
  const documentSeria = resolveCurrentMainDocValue(document.document_seria);

  if (!currentMainDoc) return "registration";
  if (documentSeria && currentMainDoc === documentSeria) return "main";
  return "switch";
};

const syncCurrentMainDoc = async (
  document: IADatabaseDocument,
): Promise<ExecuteDocumentCheckApproveResult & { currentMainDoc?: string }> => {
  const userId = resolveDocumentUserId(document);
  if (userId == null) {
    return { ok: false, message: "User ID is missing on this document." };
  }

  const documentSeria = resolveCurrentMainDocValue(document.document_seria);
  if (!documentSeria) {
    return { ok: false, message: "Document seria is missing on this document." };
  }

  const response = await admissionDatabaseRequests.updateCurrentMainDocument(userId, documentSeria);
  if (!response) {
    return { ok: false, message: "Could not update Current Document for this user." };
  }

  return { ok: true, message: "Current Document updated.", currentMainDoc: documentSeria };
};

type PreparedAccountingAction =
  | {
      ok: true;
      taskId: string;
      accountingId?: number;
      payload: IApplicantAccountingCreatePayload;
      updatePayload: IApplicantAccountingUpdatePayload;
    }
  | { ok: false; message: string };

const prepareAccountingActionFromDocument = async (
  document: IADatabaseDocument,
  mode: CurrentMainDocumentMode,
): Promise<PreparedAccountingAction> => {
  const taskIdRaw = document.task_id;
  if (taskIdRaw == null || String(taskIdRaw).trim() === "") {
    return { ok: false, message: "Task ID is missing on this document." };
  }

  const rawTask = await tasksPageRequests.fetchById(String(taskIdRaw));
  if (!rawTask) {
    return { ok: false, message: "Could not load task data for this document." };
  }

  return prepareAccountingActionFromTask(document, rawTask, String(taskIdRaw), mode);
};

const prepareAccountingActionFromTask = async (
  document: IADatabaseDocument,
  rawTask: ITaskListItem,
  taskIdHint: string,
  mode: CurrentMainDocumentMode,
): Promise<PreparedAccountingAction> => {
  const applicantInfo = parseTaskApplicantInfo(rawTask.applicant_info);
  const applicantId = parseTaskApplicantId(applicantInfo, rawTask.applicant_id);
  const taskId = resolveTaskApiId(rawTask, taskIdHint);
  const formOffice = resolveTaskFormOffice(rawTask, undefined, document.form_office ?? undefined);
  const admissionNo = resolveCurrentMainDocValue(document.document_seria);
  const dataAdmission = formatDateToApiIso(document.data_added);

  if (!applicantId) {
    return { ok: false, message: "Applicant ID is missing on this task." };
  }
  if (!taskId) {
    return {
      ok: false,
      message:
        mode === "register"
          ? "Task ID is invalid for Office Accounting create."
          : "Task ID is invalid for Office Accounting update.",
    };
  }
  if (!formOffice) {
    return {
      ok: false,
      message: "Could not determine office for this task. Applicant must have MALTA, UKRAINE, or POLAND.",
    };
  }
  if (!admissionNo || !dataAdmission) {
    return {
      ok: false,
      message: document.data_added?.trim()
        ? "Document has an invalid admission date format."
        : "Document is missing admission number or admission date.",
    };
  }

  let accountingId: number | undefined;
  if (mode === "switch") {
    const existing = await officeAccountingPageRequests.findApplicantAccountingByApplicantId(applicantId);
    const parsedId = existing?.id != null ? Number(existing.id) : null;
    if (parsedId == null || !Number.isFinite(parsedId) || parsedId <= 0) {
      return {
        ok: false,
        message: "Office Accounting record was not found for this applicant. Use Registration Doc first.",
      };
    }
    accountingId = parsedId;
  }

  const semesterCosts = buildSemesterCostsFromTask({
    admission_semester: rawTask.admission_semester,
    duration: rawTask.duration,
    tuition_fee: rawTask.tuition_fee,
    tuition_fee_flag: rawTask.tuition_fee_flag,
  });

  const payload: IApplicantAccountingCreatePayload = {
    applicant_id: applicantId,
    task_id: Number(taskId),
    form_office: formOffice as FormOfficeApi,
    admission_no: admissionNo,
    data_admission: dataAdmission,
    general_status: "Active",
    ...(semesterCosts ?? {}),
  };

  return {
    ok: true,
    taskId,
    accountingId,
    payload,
    updatePayload: buildApplicantAccountingUpdatePayload(payload, rawTask),
  };
};

/**
 * Registration Doc → POST /applicant-accounting/create + set current_main_doc.
 * Switch on Main → PUT /applicant-accounting/update + set current_main_doc.
 */
export const executeCurrentMainDocumentAction = async (
  document: IADatabaseDocument,
  mode: CurrentMainDocumentMode,
): Promise<ExecuteDocumentCheckApproveResult & { currentMainDoc?: string; userId?: number }> => {
  const userId = resolveDocumentUserId(document);
  if (userId == null) {
    return { ok: false, message: "User ID is missing on this document." };
  }

  const prepared = await prepareAccountingActionFromDocument(document, mode);
  if (!prepared.ok) return prepared;

  if (mode === "register") {
    const created = await officeAccountingPageRequests.createApplicantAccounting(prepared.payload);
    if (!created.ok) {
      return { ok: false, message: created.message };
    }

    const DONE_STATUS = "done";
    await tasksPageRequests.update(prepared.taskId, { status: DONE_STATUS });
    const doneLabel = tasksStatusConst.find((item) => item.id === DONE_STATUS)?.label ?? DONE_STATUS;

    const syncResult = await syncCurrentMainDoc(document);
    if (!syncResult.ok) {
      return {
        ok: false,
        message: `Office Accounting record was created, but Current Document could not be saved. ${syncResult.message}`,
      };
    }

    await createApplicantHistory(
      userId,
      buildSimpleHistoryText(
        "Office Accounting",
        `Record created from Registration Doc. Task marked as "${doneLabel}"`,
      ),
    );

    return {
      ok: true,
      message: `Office Accounting record created. Task marked as "${doneLabel}".`,
      currentMainDoc: syncResult.currentMainDoc,
      userId,
    };
  }

  if (prepared.accountingId == null) {
    return {
      ok: false,
      message: "Office Accounting record was not found for this applicant. Use Registration Doc first.",
    };
  }

  const updated = await officeAccountingPageRequests.updateApplicantAccounting(
    prepared.accountingId,
    prepared.updatePayload,
  );

  if (!updated) {
    return { ok: false, message: "Could not update the Office Accounting record." };
  }

  const syncResult = await syncCurrentMainDoc(document);
  if (!syncResult.ok) {
    return {
      ok: false,
      message: `Office Accounting was updated, but Current Document could not be saved. ${syncResult.message}`,
    };
  }

  await createApplicantHistory(
    userId,
    buildSimpleHistoryText("Office Accounting", "Record updated via Switch on Main"),
  );

  return {
    ok: true,
    message: "Office Accounting record updated.",
    currentMainDoc: syncResult.currentMainDoc,
    userId,
  };
};
